// Google Analytics 4, loaded only when REACT_APP_GA_MEASUREMENT_ID is set at
// build time (so local dev and any build without the ID send nothing).
//
// Sends two things:
//  - page_view on every route change (the app is a single page, so GA's own
//    automatic page view would only ever see the first page);
//  - whatsapp_click when any wa.me link is clicked, with the country (on a
//    country page) and the page it was clicked on.
//
// Privacy: the admin portal is never tracked, and the token in personal links
// (/sign/<token>, /documents/<token>…) is replaced before anything is sent,
// so a client's private link never reaches Google. Only utm_* tags are kept
// from the query string.
import { countryForPath } from './whatsapp';

const GA_ID = (process.env.REACT_APP_GA_MEASUREMENT_ID || '').trim();

const TOKEN_ROUTES = [
  '/sign/', '/verify/', '/feedback/', '/consent/', '/documents/',
  '/lessons/test/', '/lessons/', '/visa-documents/', '/client-documents/',
];

let started = false;

export function analyticsEnabled() {
  return Boolean(GA_ID);
}

function isExcluded(pathname) {
  return pathname.startsWith('/admin-portal');
}

export function safePath(pathname) {
  const prefix = TOKEN_ROUTES.find((p) => pathname.startsWith(p));
  return prefix ? `${prefix}:token` : pathname;
}

function safeLocation(pathname, search) {
  const kept = new URLSearchParams();
  new URLSearchParams(search).forEach((value, key) => {
    if (key.startsWith('utm_')) kept.set(key, value);
  });
  const query = kept.toString();
  return `${window.location.origin}${safePath(pathname)}${query ? `?${query}` : ''}`;
}

function gtag() {
  // gtag.js reads the raw `arguments` object, so this must not be an arrow
  // function or use rest parameters.
  window.dataLayer = window.dataLayer || [];
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments);
}

export function startAnalytics() {
  if (!GA_ID || started) return;
  started = true;
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_ID)}`;
  document.head.appendChild(script);
  gtag('js', new Date());
  gtag('config', GA_ID, { send_page_view: false });

  document.addEventListener('click', (e) => {
    const link = e.target.closest && e.target.closest('a[href*="wa.me/"]');
    if (!link || isExcluded(window.location.pathname)) return;
    // wa.me/?text=… share links (e.g. "share this announcement") have no
    // number: they aren't a chat with us, so they aren't counted.
    const number = (link.getAttribute('href').match(/wa\.me\/(\d+)/) || [])[1];
    if (!number) return;
    const country = countryForPath(window.location.pathname);
    gtag('event', 'whatsapp_click', {
      country: country ? country.name : '(none)',
      page_path: safePath(window.location.pathname),
      whatsapp_number: number.startsWith('263') ? 'Zimbabwe' : 'Turkey',
    });
  }, true);
}

export function trackPageView(pathname, search) {
  if (!GA_ID || isExcluded(pathname)) return;
  // Lazy routes and react-helmet-async set the new <title> a little after
  // the URL changes; wait (up to 2s) for it, so the page view carries this
  // page's title and not the previous one.
  const previousTitle = document.title;
  let waited = 0;
  const timer = setInterval(() => {
    waited += 100;
    if (document.title === previousTitle && waited < 2000) return;
    clearInterval(timer);
    gtag('event', 'page_view', {
      page_location: safeLocation(pathname, search),
      page_path: safePath(pathname),
      page_title: document.title,
    });
  }, 100);
}
